/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import { getSLTXFileContentsInLatestVersion } from './sltxVersion';
import { SLTXFormatRootFile, SLTXFormatRootFileGroup } from './sltx';
import { FilePaths } from '../../fileSystem/filePaths';
import { CatalogTagsTranslation } from '../../modelViewer/Data/catalogTagsTranslation';
import { ModelRuleData } from '../../modelConfiguration/rules/modelRuleData';
import { CustomAttributeData } from '../../customAttributes/customAttributes';
import { SLTXUtil } from './sltxUtil';

export interface CustomAttributeListExport {
  id : string,
  name: string,
  attributes: CustomAttributeData[],
}

export class SLTXFile {

  constructor() {
    this.resourcesPath = '';
    this.fileName = '';
    this.isFileOpened = false;
    this.rules = undefined;
  }

  public setFilePath(filePath : string) : void {
    [this.resourcesPath, this.fileName] = FilePaths.splitFileFromPath(filePath);
  }

  // Augment SLTX with translation to expedite UI processing later
  public setTranslation(tagsTranslations : CatalogTagsTranslation[]) {
    // eslint-disable-next-line max-len
    if (this.rootFileContents !== undefined) {
      SLTXFile.setupRootTranslation(this.rootFileContents, tagsTranslations);
    }
  }
  // Augment SLTX with translation to expedite UI processing later
  public static setupRootTranslation(
    sltxRooFile : SLTXFormatRootFile,
    tagsTranslations : CatalogTagsTranslation[],
  ) {

    const modelTrans = tagsTranslations.filter((tag) => tag.option?.split('/')[CatalogTagsTranslation.TAG_MODEL] === sltxRooFile.name);

    sltxRooFile.groups?.forEach((grp) => {
      const groupTrans = modelTrans.filter((tag) => {
        const optParts = tag.option?.split('/');
        if (optParts === undefined
          || optParts.length <= CatalogTagsTranslation.TAG_GROUP) return false;
        return (
          (
            optParts[CatalogTagsTranslation.TAG_TYPE] === 'group'
            || optParts[CatalogTagsTranslation.TAG_TYPE] === 'folder'
            || optParts[CatalogTagsTranslation.TAG_TYPE] === 'option'
          ) && (
            optParts[CatalogTagsTranslation.TAG_GROUP] === grp.name
          )
        );
      }); // modelTrans.filter
      SLTXFile.setGroupTranslation(grp, groupTrans);
    }); // forEach(grp)

  }

  // Augment SLTX with translation to expedite UI processing later
  public static setGroupTranslation(
    groupObject: SLTXFormatRootFileGroup,
    tagsTranslations : CatalogTagsTranslation[],
  ) {
    groupObject.tagTranslation = tagsTranslations.find((tag) => tag.option?.split('/')[CatalogTagsTranslation.TAG_TYPE] === 'group');

    groupObject.optionsFolders.forEach((folder) => {
      if (folder.name === 'Root') {
        // If this a root folder
        folder.tagTranslation = undefined;
        folder.options.forEach((opt) => {
          // ------------------------------------------------------------
          opt.tagTranslation = tagsTranslations.find((tag) => {
            const optParts = tag.option?.split('/');
            if (optParts === undefined
              || optParts.length <= CatalogTagsTranslation.TAG_ROOT_OPTION) return false;
            return (
              (
                optParts[CatalogTagsTranslation.TAG_TYPE] === 'option'
              ) && (
                optParts[CatalogTagsTranslation.TAG_ROOT_OPTION] === opt.name
              )
            );
          });
          if (opt.name.indexOf(SLTXUtil.OptionSectionSeparator) > 0) {
            // ------------------------------------------------------------
            // Add here the opt.tagTranslationForSection
            // ...
            // ------------------------------------------------------------
            opt.tagTranslationSection = tagsTranslations.find((tag) => {
              const optParts = tag.option?.split('/');
              if (optParts === undefined
                || optParts.length <= CatalogTagsTranslation.TAG_ROOT_OPTION) return false;
              return (
                (
                  optParts[CatalogTagsTranslation.TAG_TYPE] === 'option'
                ) && (
                  optParts[CatalogTagsTranslation.TAG_ROOT_OPTION] === `${opt.name.split(SLTXUtil.OptionSectionSeparator)[0]}${SLTXUtil.OptionSectionSeparator}`
                )
              );
            });
            // ------------------------------------------------------------
          }
        });
      } else {
        // If this a real folder
        // 1) Get folder's own tag
        folder.tagTranslation = tagsTranslations.find((tag) => {
          const optParts = tag.option?.split('/');
          if (optParts === undefined
            || optParts.length <= CatalogTagsTranslation.TAG_FOLDER) return false;
          return (
            (
              optParts[CatalogTagsTranslation.TAG_TYPE] === 'folder'
            ) && (
              optParts[CatalogTagsTranslation.TAG_FOLDER] === folder.name
            )
          );
        });

        // 2) Get childrens options tag
        folder.options.forEach((opt) => {
          opt.tagTranslation = tagsTranslations.find((tag) => {
            const optParts = tag.option?.split('/');
            if (optParts === undefined
              || optParts.length <= CatalogTagsTranslation.TAG_FOLDER_OPTION) return false;
            return (
              (
                optParts[CatalogTagsTranslation.TAG_TYPE] === 'option'
              ) && (
                optParts[CatalogTagsTranslation.TAG_FOLDER] === folder.name
                && optParts[CatalogTagsTranslation.TAG_FOLDER_OPTION] === opt.name
              )
            );
          });
          if (opt.name.indexOf(SLTXUtil.OptionSectionSeparator) > 0) {
            // ------------------------------------------------------------
            // Add here the opt.tagTranslationForSection
            // ...
            // ------------------------------------------------------------
            opt.tagTranslationSection = tagsTranslations.find((tag) => {
              const optParts = tag.option?.split('/');
              if (optParts === undefined
                || optParts.length <= CatalogTagsTranslation.TAG_FOLDER_OPTION) return false;
              return (
                (
                  optParts[CatalogTagsTranslation.TAG_TYPE] === 'option'
                ) && (
                  optParts[CatalogTagsTranslation.TAG_FOLDER] === folder.name
                  && optParts[CatalogTagsTranslation.TAG_FOLDER_OPTION] === `${opt.name.split(SLTXUtil.OptionSectionSeparator)[0]}${SLTXUtil.OptionSectionSeparator}`
                )
              );
            });
            // ------------------------------------------------------------
          }
        });
      }
    });
  }

  public setRulesTranslation(tagsTranslations : CatalogTagsTranslation[]) {

    // -----------------------------------------------------------------
    // First let's filter tags that are for this model and domain rules
    // -----------------------------------------------------------------
    const modelRulesTrans = tagsTranslations.filter((tag) => {
      const optParts = tag.option?.split('/');
      if (optParts === undefined
        || optParts.length <= CatalogTagsTranslation.TAG_DOMAIN) return false;
      return (
        (
          optParts[CatalogTagsTranslation.TAG_TYPE] === 'domain'
          || optParts[CatalogTagsTranslation.TAG_TYPE] === 'domain-group'
        ) && (
          optParts[CatalogTagsTranslation.TAG_MODEL] === this.rootFileContents?.name
        )
      );
    }); // end tagsTranslations.filter()

    // -----------------------------------------------------------------
    // Set the tag for all rules that are Domain Rules
    // -----------------------------------------------------------------
    this.rules?.forEach((rule) => {
      if (!rule.ruleDataDomain) return;

      // --------------------------------------------------------
      // Filter the tags that are for this rule
      // --------------------------------------------------------
      const ruleTags = modelRulesTrans.filter((tag) => {
        const optParts = tag.option?.split('/');
        if (optParts === undefined
          || optParts.length <= CatalogTagsTranslation.TAG_DOMAIN) return false;
        return (
          (
            optParts[CatalogTagsTranslation.TAG_TYPE] === 'domain'
            || optParts[CatalogTagsTranslation.TAG_TYPE] === 'domain-group'
          ) && (
            optParts[CatalogTagsTranslation.TAG_DOMAIN] === rule.name
          )
        );
      }); // end filter tags for this domain

      // --------------------------------------------------------
      // Set the tag for this Domain Rule
      // --------------------------------------------------------
      // eslint-disable-next-line no-param-reassign
      rule.ruleDataDomain.tagTranslation = ruleTags.find((tag) => tag.option?.split('/')[CatalogTagsTranslation.TAG_TYPE] === 'domain');

      // --------------------------------------------------------
      // Set the tag for all children domain-groups
      // --------------------------------------------------------
      rule.ruleDataDomain.domainGroups.forEach((dGroup) => {
        // eslint-disable-next-line no-param-reassign
        dGroup.tagTranslation = ruleTags.find((tag) => {
          const optParts = tag.option?.split('/');
          if (optParts === undefined
            || optParts.length <= CatalogTagsTranslation.TAG_DOMAIN_GROUP) return false;
          return (
            (
              optParts[CatalogTagsTranslation.TAG_TYPE] === 'domain-group'
            ) && (
              optParts[CatalogTagsTranslation.TAG_DOMAIN_GROUP] === dGroup.name
            )
          );
        }); // end rule.ruleDataDomain.domainGroups.forEach((dGroup)
      }); // this.catalogData?.rules.forEach((rule)
    });
  }

  public setModelName(name: string) {
    if (this.rootFileContents !== undefined) this.rootFileContents.name = name;
  }

  public static async LoadModelInfoFile(
    modeInfoFileName : string,
  ): Promise<SLTXFormatRootFile> {

    try {
      const customAttributesFilePath = FilePaths.join(
        FilePaths.splitFileFromPath(modeInfoFileName)[0],
        'customAttributes.json',
      );
      const infoResponse : Response = await fetch(modeInfoFileName);
      const infoResponseJSON = await infoResponse.json();

      const rootFileContents = getSLTXFileContentsInLatestVersion(infoResponseJSON);
      if (rootFileContents) {
        try {
          rootFileContents.customAttributes = await SLTXFile.LoadModelFlatCustomAttributesFromFile(customAttributesFilePath);
        } catch (err) {
          rootFileContents.customAttributes = [];
        }
      }
      return rootFileContents || new SLTXFormatRootFile();
    } catch (error) {
      return new SLTXFormatRootFile();
    }
  }

  // ------------------------------------------------------
  // Load model's custom attributes from specific model file
  // ------------------------------------------------------
  public static async LoadModelFlatCustomAttributesFromFile(
    modelCustomAttributesFileName : string,
  ): Promise<CustomAttributeData[]> {

    try {
      const response = await fetch(modelCustomAttributesFileName);
      if (!response.ok) {
        return [];
      }

      // Parse JSON directly from response
      const parsed = (await response.json()) as CustomAttributeListExport[];
      if (!Array.isArray(parsed)) {
        console.warn(`File '${modelCustomAttributesFileName}' does not contain an array`);
        return [];
      }
      // const flatCustAttrList : CustomAttributeData[] = [];
      const flatCustAttrList = parsed.flatMap((l) => l.attributes ?? []);

      return flatCustAttrList;
    } catch (err: any) {
      if (err.code === 'ENOENT') {
        return [];
      }
      return [];
    }
  }

  public async openFile(modelName: string) : Promise<boolean> {

    // The root of all information about the sltx file is in a file
    // named 'info.json' located in the root of the resources folder

    const rootFilePath = FilePaths.join(
      this.resourcesPath,
      this.fileName,
    );

    const translationFilePath = FilePaths.join(
      this.resourcesPath,
      'tagsTranslation.json',
    );

    const customAttributesFilePath = FilePaths.join(
      this.resourcesPath,
      'customAttributes.json',
    );

    const rulesFilePath = FilePaths.join(
      this.resourcesPath,
      'modelRules.json',
    );

    try {
      const infoResponse : Response = await fetch(rootFilePath);
      const infoResponseJSON = await infoResponse.json();

      this.rootFileContents = getSLTXFileContentsInLatestVersion(infoResponseJSON);
      this.isFileOpened = true;
      this.setModelName(modelName);
    } catch (error) {
      return false;
    }
    if (this.rootFileContents) {
      try {
        this.rootFileContents.customAttributes = await SLTXFile.LoadModelFlatCustomAttributesFromFile(customAttributesFilePath);
      } catch (err) {
        this.rootFileContents.customAttributes = [];
      }
    }

    // Load external file with translations
    try {
      const translationResponse: Response = await fetch(translationFilePath);

      if (translationResponse.ok) {
        const translationResponseJSON = await translationResponse.json();

        if (!Array.isArray(translationResponseJSON)) {
          console.warn('Model rules JSON does not contain an array');
          this.tagsTranslation = undefined;
        } else {
          this.tagsTranslation = translationResponseJSON.map((obj) => Object.assign(new CatalogTagsTranslation(), obj));
          if (this.tagsTranslation) this.setTranslation(this.tagsTranslation);
        }
      }
    } catch (err) {
      this.rules = undefined;
    }

    // Load external file with rules
    try {
      const rulesResponse: Response = await fetch(rulesFilePath);

      if (!rulesResponse.ok) {
        this.rules = undefined;
      } else {
        const rulesResponseJSON = await rulesResponse.json();

        if (!Array.isArray(rulesResponseJSON)) {
          console.warn('Model rules JSON does not contain an array');
          this.rules = undefined;
        } else {
          this.rules = rulesResponseJSON.map((obj) => Object.assign(new ModelRuleData(), obj));
          if (this.rules && this.tagsTranslation) this.setRulesTranslation(this.tagsTranslation);
        }
      }
    } catch (err) {
      this.rules = undefined;
    }

    return true;
  }

  public isOpened() : boolean {
    return this.isFileOpened;
  }

  public getRootFileContents() : SLTXFormatRootFile {
    if (this.isFileOpened === false) {
      throw new Error('File was not ready');
    }

    if (this.rootFileContents !== undefined) {
      return this.rootFileContents;
    }

    throw new Error('File was not ready');
  }

  public getCompletePath(pathFromFile : string) : string {
    return this.resourcesPath + pathFromFile;
  }

  // Resources path of the file. This is typically, the folder to
  // where the SLTX file was unzipped
  private resourcesPath : string;
  private fileName : string;

  // Flag that indicates if the file was successfully opened
  private isFileOpened : boolean;

  // File contents
  private rootFileContents : SLTXFormatRootFile | undefined;
  public rules: ModelRuleData[] | undefined;
  public tagsTranslation: CatalogTagsTranslation[] | undefined;
  // public customAttributes: CustomAttributeData[] | undefined;

}
